// "Delete version": requires the doc_uid typed, lists by name the assessments that use the version (checked up front in the assessments list for Confidential users and from the server's document-in-use refusal) and asks for a forced delete only when there are any, offers to also retire the version in the registry (off by default, so the version can be rebuilt), then starts the delete_version job.
import { useQuery } from "@tanstack/react-query";
import { TriangleAlertIcon } from "lucide-react";
import { useState } from "react";

import type { AssessmentSummary, DocumentStatus } from "@/api/__generated__";
import { assessmentsListAssessmentsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { isApiProblem } from "@/api/problem";
import { useCanAccess } from "@/app/session";
import { Mono } from "@/components/common/Mono";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckboxField } from "@/features/jobs/dialogs/CheckboxField";
import { JobDialogFrame, JobDialogLayout } from "@/features/jobs/dialogs/JobDialog";
import { useStartJob } from "@/features/jobs/useStartJob";
import { plural } from "@/lib/format";
import { DOCUMENT_STATUS_LABELS } from "@/lib/labels";

interface DeleteVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docUid: string;
  status: DocumentStatus;
}

export function DeleteVersionDialog({ open, onOpenChange, docUid, status }: DeleteVersionDialogProps) {
  return (
    <JobDialogFrame open={open} onOpenChange={onOpenChange} destructive>
      <DeleteVersionForm
        docUid={docUid}
        status={status}
        onDone={() => {
          onOpenChange(false);
        }}
      />
    </JobDialogFrame>
  );
}

export function assessmentsInUse(error: unknown): string[] {
  if (!isApiProblem(error) || error.code !== "document-in-use") {
    return [];
  }
  return (error.errors ?? []).map((item) => item.msg);
}

export interface VersionUse {
  uid: string;
  name: string | null;
}

export function versionUses(
  docUid: string,
  assessments: readonly Pick<AssessmentSummary, "assessment_uid" | "name" | "grid_code">[],
  refused: readonly string[],
): VersionUse[] {
  const names = new Map(assessments.map((item) => [item.assessment_uid, item.name]));
  const uids = new Set([
    ...assessments.filter((item) => item.grid_code.doc_uid === docUid).map((item) => item.assessment_uid),
    ...refused,
  ]);
  return [...uids]
    .map((uid) => ({ uid, name: names.get(uid) ?? null }))
    .sort((left, right) => (left.name ?? left.uid).localeCompare(right.name ?? right.uid));
}

function DeleteVersionForm({
  docUid,
  status,
  onDone,
}: {
  docUid: string;
  status: DocumentStatus;
  onDone: () => void;
}) {
  const confidential = useCanAccess("C");
  const assessments = useQuery({ ...assessmentsListAssessmentsOptions(), enabled: confidential });
  const job = useStartJob(onDone);
  const [force, setForce] = useState(false);
  const [retire, setRetire] = useState(false);
  const using = versionUses(docUid, assessments.data ?? [], assessmentsInUse(job.error));
  const blocked = using.length > 0;
  return (
    <JobDialogLayout
      title="Delete version"
      description={
        <>
          Deletes <Mono tone="ink">{docUid}</Mono> with its clauses, requirements, curves, figures and tables.
          This cannot be undone.
        </>
      }
      confirmLabel={force ? "Force delete" : "Delete version"}
      confirmText={docUid}
      canConfirm={!blocked || force}
      pending={job.pending}
      error={blocked && assessmentsInUse(job.error).length > 0 ? undefined : job.error}
      note={`Status today: ${DOCUMENT_STATUS_LABELS[status]}`}
      onConfirm={() => {
        job.start({
          kind: "delete_version",
          doc_uid: docUid,
          force: blocked && force,
          retire_registry_version: retire,
        });
      }}
    >
      {status === "in_force" ? (
        <Alert variant="attn" role="alert">
          <TriangleAlertIcon aria-hidden />
          <AlertTitle>This version is in force</AlertTitle>
          <AlertDescription>
            <p>Deleting it leaves the family without a version in force until another one is promoted.</p>
          </AlertDescription>
        </Alert>
      ) : null}
      <CheckboxField
        checked={retire}
        onCheckedChange={setRetire}
        label="Also retire the version in the registry"
        description="Without this the registry entry and the stored PDF stay, so the version can be rebuilt later."
      />
      {blocked ? (
        <>
          <Alert variant="attn" role="alert">
            <TriangleAlertIcon aria-hidden />
            <AlertTitle>{plural(using.length, "assessment")} use this version</AlertTitle>
            <AlertDescription>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {using.map((use) => (
                  <li key={use.uid}>{use.name ?? <Mono tone="ink">{use.uid}</Mono>}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
          <CheckboxField
            checked={force}
            onCheckedChange={setForce}
            label="Delete it anyway"
            description="Their results stay, but lose the links to the clauses and requirements of this version."
          />
        </>
      ) : null}
    </JobDialogLayout>
  );
}
